import { PLANTILLAS } from '@mc/formularios'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Copy,
  Eye,
  FilePlus2,
  FileText,
  Inbox,
  LayoutTemplate,
  Plus,
  Settings2,
  SquarePen,
  Trash2,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'

import { type CodigoDeIdioma, idiomaDeLaDireccion, NOMBRE_DE_IDIOMA, useIdiomas } from '../api/idiomas'
import { consulta, ErrorDeLaApi, pedir } from '../api/nucleo'
import type { Lista } from '../api/tipos'
import {
  Aviso,
  Boton,
  Esqueleto,
  EstadoVacio,
  Modal,
  Pastilla,
  Tarjeta,
  Titulo,
  clases,
} from '../componentes/ui'
import { campoPorNombre, type Esquema, useEsquema } from '../motor/esquema'
import { paginasDe, useResumenDeFormularios } from './resumen'

/**
 * Los formularios de una web (CLAUDE.md §9), como una lista que se lee de un vistazo: el
 * nombre, cuántas respuestas tiene (y cuántas sin leer), dónde sale, si admite respuestas
 * —un interruptor— y lo que se puede hacer con cada uno: editar, sus ajustes, sus
 * respuestas, la vista previa, duplicar y borrar. «Nuevo formulario» deja elegir una de
 * las plantillas del núcleo o empezar en blanco.
 */
type Formulario = {
  id: number
  nombre?: string
  slug?: string
  estado?: string
  updatedAt?: string
}

const fecha = (v?: string) =>
  v
    ? new Date(v).toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—'

export const ListaDeFormularios = ({
  espacio,
  sitio,
  base,
  descripcion,
}: {
  base: string
  descripcion?: string
  espacio: number
  sitio: number
}) => {
  const { data: esquema } = useEsquema('formularios')
  const [params, setParams] = useSearchParams()
  const navegar = useNavigate()
  const clienteDeConsultas = useQueryClient()
  const { data: resumen } = useResumenDeFormularios(sitio)
  // Un formulario va por idioma (§37): cada versión es la suya, y se listan de un idioma en uno.
  const { data: idiomasDeLaWeb } = useIdiomas(sitio)
  const idioma = idiomaDeLaDireccion(params.get('idioma'), idiomasDeLaWeb?.encendidos)
  const variosIdiomas = (idiomasDeLaWeb?.encendidos.length ?? 1) > 1
  const [aviso, setAviso] = useState<null | { texto: string; tono: 'error' | 'ok' }>(null)
  const [ocupado, setOcupado] = useState<null | number>(null)
  const { data, isPending, error } = useQuery({
    queryKey: ['lista', 'formularios', 'constructor', sitio, idioma],
    queryFn: () =>
      pedir<Lista<Formulario>>(
        `/formularios${consulta({ 'where[sitio][equals]': sitio, 'where[idioma][equals]': idioma, limit: 100, depth: 0, sort: '-updatedAt', 'select[nombre]': 'true', 'select[slug]': 'true', 'select[estado]': 'true', 'select[updatedAt]': 'true' })}`,
      ),
  })
  const formularios = data?.docs ?? []
  const nuevo = params.get('nuevo') === '1'
  const puedeCrear = esquema?.puede?.crear !== false
  const puedeBorrar = esquema?.puede?.borrar !== false
  const puedeDuplicar = esquema?.puede?.duplicar !== false && esquema?.duplicable !== false
  const estados = campoPorNombre(esquema?.campos ?? [], 'estado')?.opciones ?? []
  const conResumen = resumen instanceof Map
  // Sin el resumen del núcleo, sin sus columnas: mejor que dos columnas vacías.
  const columnas = conResumen
    ? 'md:grid-cols-[minmax(0,1fr)_8rem_10rem_10rem_7rem]'
    : 'md:grid-cols-[minmax(0,1fr)_11rem_7rem]'

  const abrirNuevo = (abrir: boolean) => {
    const siguientes = new URLSearchParams(params)
    if (abrir) siguientes.set('nuevo', '1')
    else siguientes.delete('nuevo')
    setParams(siguientes, { replace: true })
  }
  const refrescar = () => {
    void clienteDeConsultas.invalidateQueries({ queryKey: ['lista', 'formularios'] })
    void clienteDeConsultas.invalidateQueries({ queryKey: ['formularios-resumen', sitio] })
  }

  const ponerEstado = async (f: Formulario, admite: boolean) => {
    setOcupado(f.id)
    setAviso(null)
    try {
      await pedir(`/formularios/${f.id}${consulta({ depth: 0 })}`, {
        metodo: 'PATCH',
        cuerpo: { estado: admite ? 'activo' : 'cerrado' },
      })
      setAviso({
        texto: admite
          ? `«${f.nombre}» admite respuestas.`
          : `«${f.nombre}» está cerrado: en tu web enseña el mensaje de cerrado y no admite respuestas.`,
        tono: 'ok',
      })
      refrescar()
    } catch (e) {
      setAviso({
        texto: e instanceof Error ? e.message : 'No se ha podido cambiar. Vuelve a intentarlo.',
        tono: 'error',
      })
    } finally {
      setOcupado(null)
    }
  }
  const duplicar = async (f: Formulario) => {
    setOcupado(f.id)
    try {
      const r = await pedir<{ doc: Formulario }>(`/formularios/${f.id}/duplicate`, {
        metodo: 'POST',
        cuerpo: {},
      })
      refrescar()
      navegar(`${base}/${r.doc.id}`)
    } catch (e) {
      setAviso({
        texto: e instanceof Error ? e.message : 'No se ha podido duplicar.',
        tono: 'error',
      })
    } finally {
      setOcupado(null)
    }
  }
  const borrar = async (f: Formulario) => {
    if (!window.confirm(`¿Borrar «${f.nombre || 'este formulario'}»? No se puede deshacer.`)) return
    setOcupado(f.id)
    try {
      await pedir(`/formularios/${f.id}`, { metodo: 'DELETE' })
      setAviso({ texto: `«${f.nombre}» borrado.`, tono: 'ok' })
      refrescar()
    } catch (e) {
      // Con respuestas, el núcleo no lo borra y dice qué hacer (exportarlas, o cerrarlo).
      setAviso({ texto: e instanceof Error ? e.message : 'No se ha podido borrar.', tono: 'error' })
    } finally {
      setOcupado(null)
    }
  }

  return (
    <>
      <Titulo
        accion={
          puedeCrear && (
            <Boton icono={<Plus />} onClick={() => abrirNuevo(true)} tipo="primario">
              Nuevo formulario
            </Boton>
          )
        }
        descripcion={descripcion}
      >
        Formularios
      </Titulo>

      {aviso && (
        <div className="mb-4">
          <Aviso tono={aviso.tono}>{aviso.texto}</Aviso>
        </div>
      )}

      {variosIdiomas && idiomasDeLaWeb && (
        <div aria-label="Idioma" className="mb-3 flex flex-wrap gap-1" role="tablist">
          {idiomasDeLaWeb.encendidos.map((codigo) => (
            <button
              aria-selected={codigo === idioma}
              className={clases('rounded-pastilla border px-3 py-1 text-sm', codigo === idioma ? 'border-violeta bg-violeta-suave font-medium text-violeta' : 'border-linea text-tenue hover:text-tinta')}
              key={codigo}
              onClick={() => {
                const siguientes = new URLSearchParams(params)
                if (codigo === 'es') siguientes.delete('idioma')
                else siguientes.set('idioma', codigo)
                setParams(siguientes, { replace: true })
              }}
              role="tab"
              type="button"
            >
              {NOMBRE_DE_IDIOMA[codigo]}
            </button>
          ))}
        </div>
      )}

      {error ? (
        <Aviso tono="error">
          No se ha podido cargar la lista de formularios. Vuelve a intentarlo.
        </Aviso>
      ) : isPending ? (
        <Tarjeta sinRelleno>
          <Esqueleto />
        </Tarjeta>
      ) : formularios.length === 0 ? (
        <EstadoVacio
          accion={
            puedeCrear && (
              <Boton icono={<Plus />} onClick={() => abrirNuevo(true)} tipo="primario">
                Crear el primero
              </Boton>
            )
          }
          descripcion="Contacto, presupuesto, reserva de cita, encuesta… Empieza con una plantilla y cámbiala a tu gusto. Lo que llegue lo verás en Respuestas."
          icono={<FileText />}
          titulo="Todavía no tienes formularios"
        />
      ) : (
        <Tarjeta sinRelleno>
          <div
            className={clases(
              'hidden gap-4 border-b border-linea bg-papel-2 px-5 py-2.5 text-xs font-medium text-tenue md:grid',
              columnas,
            )}
          >
            <span>Formulario</span>
            {conResumen && <span>Respuestas</span>}
            {conResumen && <span>Dónde sale</span>}
            <span>Admite respuestas</span>
            <span>Modificado</span>
          </div>
          <ul>
            {formularios.map((f) => {
              const datos = resumen instanceof Map ? resumen.get(f.id) : undefined
              const paginas = paginasDe(datos?.paginas)
              const editor = `${base}/${f.id}`
              const etiquetaDeEstado =
                estados.find((e) => e.valor === f.estado)?.etiqueta ?? f.estado
              return (
                <li
                  className={clases(
                    'grid gap-x-4 gap-y-2 border-b border-linea px-5 py-4 last:border-b-0 md:items-center',
                    columnas,
                  )}
                  key={f.id}
                >
                  <div className="min-w-0">
                    <Link
                      className="text-[15px] font-medium text-tinta hover:text-violeta"
                      to={editor}
                    >
                      {f.nombre || <span className="text-tenue">Sin nombre</span>}
                    </Link>
                    {f.slug && <span className="ml-2 font-mono text-xs text-tenue">{f.slug}</span>}
                    <nav
                      aria-label={`Acciones de «${f.nombre}»`}
                      className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px]"
                    >
                      <AccionDeFila icono={<SquarePen />} to={editor}>
                        Editar
                      </AccionDeFila>
                      <AccionDeFila icono={<Settings2 />} to={`${editor}#ajustes`}>
                        Ajustes
                      </AccionDeFila>
                      <AccionDeFila icono={<Inbox />} to={`${editor}#respuestas`}>
                        Respuestas
                      </AccionDeFila>
                      <AccionDeFila icono={<Eye />} to={`${editor}#vista-previa`}>
                        Vista previa
                      </AccionDeFila>
                      {puedeDuplicar && (
                        <button
                          className="inline-flex items-center gap-1 text-tinta-2 hover:text-violeta disabled:text-tenue [&>svg]:size-3.5"
                          disabled={ocupado === f.id}
                          onClick={() => void duplicar(f)}
                          type="button"
                        >
                          <Copy aria-hidden /> Duplicar
                        </button>
                      )}
                      {puedeBorrar && (
                        <button
                          className="inline-flex items-center gap-1 text-tinta-2 hover:text-error disabled:text-tenue [&>svg]:size-3.5"
                          disabled={ocupado === f.id}
                          onClick={() => void borrar(f)}
                          type="button"
                        >
                          <Trash2 aria-hidden /> Borrar
                        </button>
                      )}
                    </nav>
                  </div>
                  {conResumen && (
                    <div className="text-sm">
                      {datos && typeof datos.respuestas === 'number' && (
                        <Link
                          className="inline-flex flex-wrap items-center gap-1.5 hover:text-violeta"
                          to={`${editor}#respuestas`}
                        >
                          <span className="font-medium tabular-nums">
                            {datos.respuestas.toLocaleString('es')}
                          </span>
                          <span className="text-tenue md:hidden">respuestas</span>
                          {Boolean(datos.nuevas) && (
                            <Pastilla clave="violeta" punto={false}>
                              {datos.nuevas} {datos.nuevas === 1 ? 'nueva' : 'nuevas'}
                            </Pastilla>
                          )}
                        </Link>
                      )}
                    </div>
                  )}
                  {conResumen && (
                    <div className="text-sm text-tinta-2">
                      {paginas &&
                        (paginas.cuantas === 0 ? (
                          <span className="text-tenue">En ninguna página</span>
                        ) : (
                          <span title={paginas.titulos.join(', ') || undefined}>
                            {paginas.titulos.length === 1
                              ? paginas.titulos[0]
                              : `${paginas.cuantas} ${paginas.cuantas === 1 ? 'página' : 'páginas'}`}
                          </span>
                        ))}
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Interruptor
                      activo={f.estado === 'activo'}
                      disabled={ocupado === f.id}
                      etiqueta={`«${f.nombre || 'Formulario'}» admite respuestas`}
                      onCambiar={(v) => void ponerEstado(f, v)}
                    />
                    <span className="text-xs text-tenue">{etiquetaDeEstado}</span>
                  </div>
                  <span className="text-xs text-tenue tabular-nums">{fecha(f.updatedAt)}</span>
                </li>
              )
            })}
          </ul>
        </Tarjeta>
      )}

      {nuevo && esquema && puedeCrear && (
        <NuevoFormulario
          base={base}
          cerrar={() => abrirNuevo(false)}
          espacio={espacio}
          esquema={esquema}
          existentes={formularios.map((f) => f.nombre ?? '')}
          idioma={idioma}
          sitio={sitio}
        />
      )}
    </>
  )
}

const AccionDeFila = ({
  to,
  icono,
  children,
}: {
  children: string
  icono: ReactNode
  to: string
}) => (
  <Link
    className="inline-flex items-center gap-1 text-tinta-2 hover:text-violeta [&>svg]:size-3.5"
    to={to}
  >
    {icono}
    {children}
  </Link>
)

/** Un interruptor de verdad (`role="switch"`): se usa con el ratón, con el dedo y con la barra espaciadora. */
const Interruptor = ({
  activo,
  onCambiar,
  etiqueta,
  disabled,
}: {
  activo: boolean
  disabled?: boolean
  etiqueta: string
  onCambiar: (v: boolean) => void
}) => (
  <button
    aria-checked={activo}
    aria-label={etiqueta}
    className={clases(
      'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-60',
      activo ? 'bg-violeta' : 'bg-linea-fuerte',
    )}
    disabled={disabled}
    onClick={() => onCambiar(!activo)}
    role="switch"
    type="button"
  >
    <span
      aria-hidden
      className={clases(
        'inline-block size-4 rounded-full bg-papel shadow-suave transition-transform',
        activo ? 'translate-x-[18px]' : 'translate-x-0.5',
      )}
    />
  </button>
)

/** Un nombre que no repite el de otro formulario de la web: «Contacto», y si ya está, «Contacto 2». */
export const nombreLibre = (base: string, existentes: string[]): string => {
  const usados = new Set(existentes.map((n) => n.trim().toLowerCase()))
  if (!usados.has(base.toLowerCase())) return base
  for (let n = 2; n < 100; n++) if (!usados.has(`${base} ${n}`.toLowerCase())) return `${base} ${n}`
  return base
}

type Opcion = { valor: string; nombre: string; descripcion: string; campos: string[] }

/**
 * «Nuevo formulario»: un nombre y de dónde partir. Las plantillas son las que ofrece el
 * núcleo (el campo `plantilla` del esquema), que las copia al crear; su nombre, su
 * descripción y sus campos se enseñan con `@mc/formularios`, el mismo catálogo. Al crear, se
 * abre el constructor.
 */
const NuevoFormulario = ({
  esquema,
  existentes,
  espacio,
  sitio,
  base,
  cerrar,
  idioma,
}: {
  base: string
  cerrar: () => void
  espacio: number
  esquema: Esquema
  existentes: string[]
  /** El de la pestaña: «Nuevo» en la del inglés crea un formulario que solo existe en inglés. */
  idioma: CodigoDeIdioma
  sitio: number
}) => {
  const navegar = useNavigate()
  const clienteDeConsultas = useQueryClient()
  const plantillas: Opcion[] = (campoPorNombre(esquema.campos, 'plantilla')?.opciones ?? []).map(
    (o) => {
      const deLaLibreria = PLANTILLAS.find((p) => p.slug === o.valor)
      const [nombre, descripcion] = o.etiqueta.split(' — ')
      return {
        valor: o.valor,
        nombre: deLaLibreria?.nombre ?? nombre ?? o.etiqueta,
        descripcion: deLaLibreria?.descripcion ?? descripcion ?? '',
        campos: (deLaLibreria?.campos ?? [])
          .filter((c) => c.tipo !== 'oculto' && c.tipo !== 'paso')
          .map((c) => c.etiqueta),
      }
    },
  )
  const opciones: Opcion[] = [
    {
      valor: '',
      nombre: 'En blanco',
      descripcion: 'Sin campos: añade tú los que quieras.',
      campos: [],
    },
    ...plantillas,
  ]
  const [elegida, setElegida] = useState(plantillas[0]?.valor ?? '')
  const nombreDe = (valor: string) => {
    const opcion = opciones.find((o) => o.valor === valor)
    return nombreLibre(valor && opcion ? opcion.nombre : 'Formulario nuevo', existentes)
  }
  const [nombre, setNombre] = useState(() => nombreDe(plantillas[0]?.valor ?? ''))
  const [tocado, setTocado] = useState(false)
  const [creando, setCreando] = useState(false)
  const [fallo, setFallo] = useState<null | string>(null)

  const crear = async () => {
    if (!nombre.trim()) {
      setFallo('Ponle un nombre: es como lo reconocerás en la lista.')
      return
    }
    setCreando(true)
    setFallo(null)
    try {
      const r = await pedir<{ doc: { id: number } }>(`/formularios${consulta({ depth: 0 })}`, {
        metodo: 'POST',
        cuerpo: {
          nombre: nombre.trim(),
          cliente: espacio,
          sitio,
          ...(idioma !== 'es' ? { idioma } : {}),
          ...(elegida ? { plantilla: elegida } : {}),
        },
      })
      void clienteDeConsultas.invalidateQueries({ queryKey: ['lista', 'formularios'] })
      void clienteDeConsultas.invalidateQueries({ queryKey: ['formularios-resumen', sitio] })
      navegar(`${base}/${r.doc.id}`, { replace: true })
    } catch (e) {
      const choca =
        e instanceof ErrorDeLaApi &&
        Array.isArray(e.datos.errors) &&
        (e.datos.errors as Array<{ path?: string }>).some((x) => x.path === 'slug')
      setFallo(
        choca
          ? 'Ya tienes un formulario con ese nombre. Ponle otro.'
          : e instanceof Error
            ? e.message
            : 'No se ha podido crear. Vuelve a intentarlo.',
      )
      setCreando(false)
    }
  }

  return (
    <Modal
      ancho="max-w-3xl"
      cerrar={cerrar}
      pie={
        <>
          <Boton disabled={creando} onClick={cerrar} tipo="discreto">
            Cancelar
          </Boton>
          <Boton
            cargando={creando}
            icono={<FilePlus2 />}
            onClick={() => void crear()}
            tipo="primario"
          >
            Crear y abrir el editor
          </Boton>
        </>
      }
      titulo="Nuevo formulario"
    >
      <form
        className="flex flex-col gap-5"
        onSubmit={(e) => {
          e.preventDefault()
          void crear()
        }}
      >
        {fallo && <Aviso tono="error">{fallo}</Aviso>}
        <label className="flex flex-col gap-1.5">
          <span className="text-[13px] font-medium">Nombre</span>
          <input
            className="campo h-10 w-full rounded-campo border border-linea bg-papel px-3 text-[15px] hover:border-linea-fuerte"
            data-autofocus
            onChange={(e) => {
              setNombre(e.target.value)
              setTocado(true)
            }}
            value={nombre}
          />
          <span className="text-xs text-tenue">
            Para reconocerlo tú en la lista y en Respuestas. Quien rellena el formulario no lo ve.
          </span>
        </label>
        <fieldset>
          <legend className="mb-2 text-[13px] font-medium">De dónde partir</legend>
          <div className="grid gap-2.5 sm:grid-cols-2" role="radiogroup">
            {opciones.map((o) => {
              const marcada = elegida === o.valor
              return (
                <button
                  aria-checked={marcada}
                  className={clases(
                    'flex flex-col items-start gap-1.5 rounded-caja border p-3.5 text-left transition-colors',
                    marcada
                      ? 'border-violeta bg-violeta-suave/40 ring-3 ring-violeta/10'
                      : 'border-linea bg-papel hover:border-linea-fuerte',
                  )}
                  key={o.valor || 'en-blanco'}
                  onClick={() => {
                    setElegida(o.valor)
                    if (!tocado) setNombre(nombreDe(o.valor))
                  }}
                  role="radio"
                  type="button"
                >
                  <span className="flex items-center gap-2">
                    <span
                      className={clases(
                        'flex size-7 items-center justify-center rounded-boton [&>svg]:size-4',
                        marcada ? 'bg-violeta text-white' : 'bg-papel-3 text-tinta-2',
                      )}
                    >
                      {o.valor ? <LayoutTemplate aria-hidden /> : <FilePlus2 aria-hidden />}
                    </span>
                    <span className="font-medium">{o.nombre}</span>
                  </span>
                  {o.descripcion && (
                    <span className="text-[13px] text-tinta-2">{o.descripcion}</span>
                  )}
                  {o.campos.length > 0 && (
                    <span className="flex flex-wrap gap-1">
                      {o.campos.slice(0, 4).map((c) => (
                        <span
                          className="rounded-pastilla bg-papel-3 px-1.5 py-0.5 text-[11px] text-tinta-2"
                          key={c}
                        >
                          {c}
                        </span>
                      ))}
                      {o.campos.length > 4 && (
                        <span className="px-1 py-0.5 text-[11px] text-tenue">
                          y {o.campos.length - 4} más
                        </span>
                      )}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </fieldset>
        <button className="hidden" type="submit" />
      </form>
    </Modal>
  )
}
